import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Fab, Tooltip, Box, Fade, Button, useTheme } from '@mui/material';
import { Schema, Close } from '@mui/icons-material';
import { useSetAtom } from 'jotai';

import { ChatWindow } from './components/chat/ChatWindow';
import { GraphVisualizer } from './components/visualizer/GraphVisualizer';
import { ChatbotSetupWizard } from './components/chat/ChatbotSetupWizard';
import { chatbotConfigState } from './state/config/ChatbotConfigState';
import { useAuth } from '../context/AuthContext';
import { getChatbotConfig } from './utils/server/storageUtils';
import { getActiveChatbotId, getActiveTenantId } from './utils/server/authUtils';
import { useAppTranslation } from './hooks/useAppTranslation';
import { TRANSLATIONS } from './utils/constants/translations';

const CONFIG_READY_EVENT = 'chatbot-config-ready';
const CONFIG_CACHE_PREFIX = 'chatbot_config_cache:';


function isValidUuid(value) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        String(value || '').trim()
    );
}

function sanitizeChatbotId(value = null) {
    return isValidUuid(value) ? String(value).trim() : null;
}

const DEFAULT_RUNTIME_CONFIG = {
    chatbotName: '',
    primaryColor: '#172983',
    secondaryColor: '#068945',
    fontFamily: 'Roboto',
    language: 'de',
    modelType: 'fast',
    chatWallpaper: 'none',
    logoUrl: '',
    welcomeMessage: '',
    hasCompletedOnboarding: false,
    onboardingCompletedAt: null,
    setupSource: null,
};

function safeJsonParse(value) {
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
}

function decodeBase64Url(value = '') {
    try {
        const normalized = String(value || '')
            .replace(/-/g, '+')
            .replace(/_/g, '/')
            .padEnd(Math.ceil(String(value || '').length / 4) * 4, '=');

        if (typeof window !== 'undefined' && typeof window.atob === 'function') {
            return window.atob(normalized);
        }

        return '';
    } catch {
        return '';
    }
}

function getUserCacheScope(token = null) {
    const parts = String(token || '').split('.');
    if (parts.length < 2) return 'anonymous';

    const payload = safeJsonParse(decodeBase64Url(parts[1]));
    return payload?.sub || payload?.user_id || payload?.userId || 'anonymous';
}

function getConfigCacheKey({ token = null, tenantId = null, chatbotId = null } = {}) {
    const resolvedTenantId = tenantId || (token ? getActiveTenantId({ token }) : null) || 'no-tenant';
    const resolvedChatbotId = sanitizeChatbotId(
        chatbotId || (token ? getActiveChatbotId({ token, tenantId: resolvedTenantId }) : null)
    ) || 'no-chatbot';
    const userScope = getUserCacheScope(token);

    return `${CONFIG_CACHE_PREFIX}${userScope}:${resolvedTenantId}:${resolvedChatbotId}`;
}

function readCachedConfig(scope = {}) {
    if (typeof window === 'undefined') return null;

    try {
        const raw = window.localStorage.getItem(getConfigCacheKey(scope));
        if (!raw) return null;
        return JSON.parse(raw);
    } catch (error) {
        console.error('Failed to read cached chatbot config:', error);
        return null;
    }
}

function writeCachedConfig(config, scope = {}) {
    if (typeof window === 'undefined' || !config) return;

    try {
        window.localStorage.setItem(getConfigCacheKey(scope), JSON.stringify(config));
    } catch (error) {
        console.error('Failed to cache chatbot config:', error);
    }
}


function withDefaultWelcome(config = {}) {
    const lang = config?.language === 'en' ? 'en' : 'de';
    const defaultWelcome = (TRANSLATIONS[lang] || TRANSLATIONS.de)?.defaultWelcomeMessage || '';
    return {
        ...config,
        language: lang,
        chatbotName: config?.chatbotName || (TRANSLATIONS[lang] || TRANSLATIONS.de)?.defaultChatbotName || '',
        welcomeMessage: config?.welcomeMessage || defaultWelcome,
    };
}

function normalizeConfig(config = {}) {
    return {
        ...withDefaultWelcome(config),
        hasCompletedOnboarding: Boolean(config?.hasCompletedOnboarding),
    };
}

export default function ChatInterface() {
    const { t } = useAppTranslation();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' ? window.innerWidth <= 900 : false
    );
    const [showVisualizer, setShowVisualizer] = useState(false);
    const [isCheckingSetup, setIsCheckingSetup] = useState(true);
    const [showSetupWizard, setShowSetupWizard] = useState(false);
    const [loadedConfig, setLoadedConfig] = useState(null);
    const [configReloadKey, setConfigReloadKey] = useState(0);

    const setConfig = useSetAtom(chatbotConfigState);
    const { token } = useAuth();

    const secondaryColor = loadedConfig?.secondaryColor || '#068945';

    const activeTenantScope = (token ? getActiveTenantId({ token }) : null) || null;
    const activeChatbotScope = sanitizeChatbotId(
        token ? getActiveChatbotId({ token, tenantId: activeTenantScope }) : null
    ) || null;
    const activeScope = useMemo(() => ({
        token,
        tenantId: activeTenantScope,
        chatbotId: activeChatbotScope,
    }), [activeChatbotScope, activeTenantScope, token]);
    const chatWindowScopeKey = `${activeTenantScope || 'no-tenant'}:${activeChatbotScope || 'no-chatbot'}:${configReloadKey}`;

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 900);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    useEffect(() => {
        if (typeof window === 'undefined') return undefined;

        const handleScopeChange = () => {
            setConfigReloadKey((value) => value + 1);
        };

        window.addEventListener('active-chatbot-changed', handleScopeChange);
        window.addEventListener('active-tenant-changed', handleScopeChange);

        return () => {
            window.removeEventListener('active-chatbot-changed', handleScopeChange);
            window.removeEventListener('active-tenant-changed', handleScopeChange);
        };
    }, []);

    useEffect(() => {
        let isMounted = true;

        const finalizeReady = () => {
            if (typeof window !== 'undefined') {
                window.__CHATBOT_CONFIG_READY__ = true;
                window.dispatchEvent(new Event(CONFIG_READY_EVENT));
            }
        };

        const applyResolvedConfig = (nextConfig) => {
            if (!isMounted) return;

            const normalizedConfig = normalizeConfig(nextConfig);

            setLoadedConfig(normalizedConfig);
            setConfig((prev) => ({ ...prev, ...normalizedConfig }));
            setShowSetupWizard(!normalizedConfig.hasCompletedOnboarding);
            setIsCheckingSetup(false);

            finalizeReady();
            writeCachedConfig(normalizedConfig, activeScope);
        };

        const applySafeFallback = () => {
            if (!isMounted) return;

            const fallbackConfig = normalizeConfig(DEFAULT_RUNTIME_CONFIG);

            setLoadedConfig(fallbackConfig);
            setConfig(() => ({ ...fallbackConfig }));
            setShowSetupWizard(!fallbackConfig.hasCompletedOnboarding);
            setIsCheckingSetup(false);
            finalizeReady();
        };

        const loadConfig = async () => {
            if (!token) {
                applySafeFallback();
                return;
            }

            setIsCheckingSetup(true);

            try {
                const savedConfig = await getChatbotConfig(token, activeChatbotScope, activeTenantScope);
                applyResolvedConfig(savedConfig || {});
            } catch (error) {
                console.error('Failed to load chatbot config in ChatInterface:', error);

                const cachedConfig = readCachedConfig(activeScope);

                if (cachedConfig) {
                    applyResolvedConfig(cachedConfig);
                    return;
                }

                // If backend config loading fails and there is no cache,
                // reset to a safe default so the previous tenant/chatbot theme does not bleed through.
                applySafeFallback();
            }
        };

        loadConfig();

        return () => {
            isMounted = false;
        };
    }, [activeChatbotScope, activeTenantScope, activeScope, configReloadKey, token, setConfig]);

    const handleSetupCompleted = (savedConfig) => {
        const normalizedConfig = normalizeConfig({
            ...(savedConfig || {}),
            hasCompletedOnboarding: true,
        });

        setLoadedConfig(normalizedConfig);
        setConfig((prev) => ({ ...prev, ...normalizedConfig }));
        setShowSetupWizard(false);
        setIsCheckingSetup(false);

        if (typeof window !== 'undefined') {
            window.__CHATBOT_CONFIG_READY__ = true;
            window.dispatchEvent(new Event(CONFIG_READY_EVENT));
        }

        writeCachedConfig(normalizedConfig, activeScope);
    };

    return (
        <Box
            sx={{
                position: 'relative',
                height: 'calc(100vh - 100px)',
                overflow: 'hidden',
                background: isDark 
                    ? `radial-gradient(circle at top left, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 36%), linear-gradient(135deg, ${secondaryColor} 0%, #0f172a 100%)`
                    : `radial-gradient(circle at top left, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 36%), linear-gradient(135deg, ${secondaryColor}15 0%, #f8fafc 100%)`,
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    pointerEvents: 'none',
                    '&::before, &::after': {
                        content: '""',
                        position: 'absolute',
                        borderRadius: '50%',
                        filter: 'blur(70px)',
                        opacity: 0.28,
                    },
                    '&::before': {
                        width: 260,
                        height: 260,
                        top: 40,
                        left: 40,
                        backgroundColor: '#93c5fd',
                    },
                    '&::after': {
                        width: 320,
                        height: 320,
                        right: 40,
                        bottom: 30,
                        backgroundColor: '#86efac',
                    },
                }}
            />

            <Box
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    height: '100%',
                    width: '100%',
                    px: { xs: 0, sm: 2 },
                    pt: { xs: 0, md: 3 },
                    pb: 0,
                }}
            >
                <Box
                    sx={{
                        height: '100%',
                        width: '100%',
                        maxWidth: '1600px',
                        mx: 'auto',
                    }}
                >
                    <Grid
                        container
                        spacing={2.5}
                        justifyContent='center'
                        alignItems='stretch'
                        wrap={isMobile ? 'wrap' : 'nowrap'}
                        sx={{ height: '100%', width: '100%', m: 0 }}
                    >
                        <Grid
                            item
                            xs={12}
                            md={showVisualizer ? 8 : 12}
                            lg={showVisualizer ? 8 : 12}
                            sx={{
                                height: isMobile ? '100%' : '92%',
                                maxHeight: isMobile ? 'none' : 'unset',
                                display: 'flex',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                width: '100%',
                                minWidth: 0,
                                maxWidth: showVisualizer ? 'none' : '1200px',
                                transition: 'all 0.35s ease',
                            }}
                        >
                            <ChatWindow key={`chat-window-${chatWindowScopeKey}`} isMobile={isMobile} />
                        </Grid>

                        {isMobile && !showSetupWizard && !isCheckingSetup && (
                            <Grid item xs={12}>
                                <Box sx={{ px: 1.25, pt: 1 }}>
                                    <Button
                                        fullWidth
                                        variant='contained'
                                        startIcon={showVisualizer ? <Close /> : <Schema />}
                                        onClick={() => setShowVisualizer(!showVisualizer)}
                                        sx={{
                                            minHeight: 48,
                                            borderRadius: '14px',
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            boxShadow: '0 14px 28px rgba(15, 23, 42, 0.18)',
                                        }}
                                    >
                                        {showVisualizer ? t.hideVisualMap : t.showVisualMap}
                                    </Button>
                                </Box>
                            </Grid>
                        )}

                        {isMobile && showVisualizer && !showSetupWizard && (
                            <Grid item xs={12}>
                                <Fade in={showVisualizer} timeout={300}>
                                    <Box sx={{ px: 1.25, pt: 1, pb: 2 }}>
                                        <Box
                                            sx={{
                                                width: '100%',
                                                minHeight: 340,
                                                height: 'min(52vh, 460px)',
                                                borderRadius: 3,
                                                overflow: 'hidden',
                                                border: '1px solid rgba(255,255,255,0.14)',
                                                boxShadow: '0 18px 36px rgba(15, 23, 42, 0.20)',
                                                backgroundColor: 'rgba(255,255,255,0.04)',
                                            }}
                                        >
                                            <GraphVisualizer highlightNodeId={null} />
                                        </Box>
                                    </Box>
                                </Fade>
                            </Grid>
                        )}

                        {!isMobile && showVisualizer && !showSetupWizard && (
                            <Grid
                                item
                                xs={12}
                                md={4}
                                lg={4}
                                sx={{
                                    height: '92%',
                                    minWidth: 0,
                                    display: 'flex',
                                }}
                            >
                                <Fade in={showVisualizer} timeout={450}>
                                    <Box sx={{ height: '100%', width: '100%', minWidth: 0 }}>
                                        <GraphVisualizer highlightNodeId={null} />
                                    </Box>
                                </Fade>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </Box>

            {!isMobile && !showSetupWizard && !isCheckingSetup && (
                <Tooltip title={showVisualizer ? t.hideVisualMap : t.showVisualMap}>
                    <Fab
                        color='primary'
                        aria-label='visualizer'
                        sx={{
                            position: 'absolute',
                            bottom: 35,
                            right: 35,
                            boxShadow: '0 18px 35px rgba(15, 23, 42, 0.22)',
                            transition: 'all 0.2s ease',
                            '&:hover': { transform: 'translateY(-2px) scale(1.03)' },
                        }}
                        onClick={() => setShowVisualizer(!showVisualizer)}
                    >
                        {showVisualizer ? <Close /> : <Schema />}
                    </Fab>
                </Tooltip>
            )}

            <ChatbotSetupWizard
                open={!isCheckingSetup && showSetupWizard}
                initialConfig={loadedConfig}
                onCompleted={handleSetupCompleted}
            />
        </Box>
    );
}